// wallpaperLibrary/toDataUri.js
//
// Turn a stocked wallpaper preset into a `data:image/svg+xml,...` URL that the
// existing wallpaper picker stores in the `bgWallpaper` image slot (currentBg
// === 2) and renders both as an <img src> thumbnail AND as the wheel/segment
// background image. Self-describing: the SVG carries a `data-iyuga-wallpaper`
// marker so a saved value can be recognised + the preset id recovered.

import {
  generateWallpaperSvg,
  WALLPAPER_PRESET_BY_ID,
  WALLPAPER_PRESETS,
} from "./wallpaperGenerators";

// Correct encoding for embedding raw SVG in a data-URI that is usable both as an
// <img src> AND inside an UNQUOTED CSS `url(...)`. Every real render path applies
// bgWallpaper unquoted (`background-image: url(${bgWallpaper})`), so the value MUST
// be url()-safe with NO reliance on wrapping quotes: literal spaces, quotes and
// parentheses in an unquoted url() are a parse error -> `background-image: none`
// (the picker thumbnail still shows, but the wheel/segment background goes blank).
// We therefore percent-encode space / ' / ( ) in addition to the structural chars.
// Percent-encoding is transparent — the browser decodes the payload back to real
// SVG before parsing, so the SVG's own internal `url(#gradient)` refs survive.
// Encode `%` FIRST (so later %XX sequences aren't double-encoded).
export const encodeSvg = (svg) =>
  svg
    .replace(/\n/g, "")
    .replace(/%/g, "%25")
    .replace(/"/g, "'") // double quotes -> single (smaller, then encoded below)
    .replace(/'/g, "%27")
    .replace(/ /g, "%20")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/#/g, "%23")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/&/g, "%26")
    .replace(/{/g, "%7B")
    .replace(/}/g, "%7D");

// Build the data-URI for a preset id, or "" for an unknown id.
export const buildWallpaperDataUri = (id) => {
  const svg = generateWallpaperSvg(id);
  if (!svg) return "";
  return `data:image/svg+xml,${encodeSvg(svg)}`;
};

// True when a stored bgWallpaper value is one of our generated library wallpapers.
export const isWallpaperLibraryUri = (value) =>
  typeof value === "string" &&
  value.includes("data:image/svg+xml") &&
  value.includes("data-iyuga-wallpaper");

// Recover the preset id from a stored library data-URI, or null if it isn't one.
// The marker's quote chars are percent-encoded by encodeSvg (" -> ' -> %27), so
// accept a raw or a percent-encoded (%27 / %22) delimiter before the id.
export const parseWallpaperUri = (value) => {
  if (!isWallpaperLibraryUri(value)) return null;
  const m = value.match(
    /data-iyuga-wallpaper=(?:['"]|%27|%22)([a-z]+-[a-z]+)(?:['"]|%27|%22)/i
  );
  const id = m && m[1];
  return id && WALLPAPER_PRESET_BY_ID[id] ? id : null;
};

// The stocked library the picker renders: [{ id, label, dataUri }]. Built once
// (module-level) — the 40 data-URIs are computed a single time and reused.
export const getWallpaperLibrary = () =>
  WALLPAPER_PRESETS.map((p) => ({
    id: p.id,
    label: p.label,
    paletteId: p.paletteId,
    motifId: p.motifId,
    dataUri: buildWallpaperDataUri(p.id),
  }));

// Frozen singleton so consumers never recompute the 40 URIs per render.
export const WALLPAPER_LIBRARY = getWallpaperLibrary();
